<script setup lang="ts">
import { createNewChartConfigurationPlaceholder } from '@/stores/chartConfiguration'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'

const props = defineProps<{
  chartConfigurations: ChartConfiguration[]
}>()

const emit = defineEmits<{
  select: [config: ChartConfiguration]
  delete: [config: ChartConfiguration]
  download: [config: ChartConfiguration]
}>()

const canDelete = () => props.chartConfigurations.length > 1
</script>

<template>
  <div class="chart-list-view">
    <div class="chart-grid">
      <v-card
        v-for="config in props.chartConfigurations"
        :key="config.id"
        class="chart-card outlined-card"
        hover
        @click="emit('select', config)"
      >
        <div class="chart-thumbnail">
          <v-icon size="64" color="cyan-darken-1">mdi-chart-bar-stacked</v-icon>
        </div>
        <div class="chart-card-footer px-3 pb-2 pt-1 d-flex align-center justify-space-between">
          <span class="text-subtitle-2 chart-card-title">{{ config.name }}</span>
          <div class="chart-card-actions">
            <v-btn icon size="x-small" variant="text" @click.stop="emit('download', config)">
              <v-icon size="16">mdi-download</v-icon>
              <v-tooltip activator="parent" location="top">Download chart</v-tooltip>
            </v-btn>
            <v-btn
              icon
              size="x-small"
              variant="text"
              :disabled="!canDelete()"
              @click.stop="emit('delete', config)"
            >
              <v-icon size="16">mdi-delete</v-icon>
              <v-tooltip activator="parent" location="top">
                {{ canDelete() ? 'Delete chart' : 'Cannot delete the last chart' }}
              </v-tooltip>
            </v-btn>
          </div>
        </div>
      </v-card>

      <!-- Add new chart placeholder card -->
      <v-card
        class="chart-card outlined-card chart-card--add"
        hover
        @click="emit('select', createNewChartConfigurationPlaceholder())"
      >
        <div class="chart-thumbnail">
          <v-icon size="48" color="cyan-darken-1">mdi-plus-circle-outline</v-icon>
        </div>
        <v-card-title class="chart-card-title text-subtitle-2 pt-2 pb-3 px-3 text-cyan-darken-1">
          Add another chart
        </v-card-title>
      </v-card>
    </div>
  </div>
</template>

<style scoped>
.chart-list-view {
  padding: 8px 0;
}

.chart-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-card {
  width: 220px;
  cursor: pointer;
  border: 1px solid #c0c0c0;
  border-radius: 10px;
  transition: box-shadow 0.15s;
}

.chart-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.chart-card--add {
  border-style: dashed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chart-thumbnail {
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5faff;
  border-radius: 10px 10px 0 0;
}

.chart-card--add .chart-thumbnail {
  background: transparent;
}

.chart-card-footer {
  min-height: 36px;
}

.chart-card-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.chart-card-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
